import styled, { css } from 'styled-components';

// Media query breakpoints
const breakpoints = {
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px'
};

// Media query helper
const media = Object.keys(breakpoints).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${breakpoints[label]}) {
      ${css(...args)}
    }
  `;
  return acc;
}, {});

// Base Button component
export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.text.inverse};
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.typography.fontSizes.sm};
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  font-family: ${props => props.theme.typography.fontFamily};
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
  margin: ${props => props.margin || '0'};
  white-space: ${props => props.nowrap ? 'nowrap' : 'normal'};
  box-shadow: ${props => props.theme.shadows.small};
  text-decoration: none;
  user-select: none;

  ${({ variant, theme }) => variant === 'secondary' && css`
    background-color: ${theme.colors.secondary};
    box-shadow: ${theme.shadows.small};
  `}

  ${({ variant, theme }) => variant === 'success' && css`
    background-color: ${theme.colors.success};
    box-shadow: ${theme.shadows.small};
  `}

  ${({ variant, theme }) => variant === 'warning' && css`
    background-color: ${theme.colors.warning};
    color: ${theme.colors.text.primary};
    box-shadow: ${theme.shadows.small};
  `}

  ${({ variant, theme }) => variant === 'error' && css`
    background-color: ${theme.colors.error};
    box-shadow: ${theme.shadows.small};
  `}

  ${({ variant, theme }) => variant === 'outline' && css`
    background-color: transparent;
    border: 1px solid ${theme.colors.primary};
    color: ${theme.colors.primary};
    box-shadow: none;
  `}

  ${({ variant, theme }) => variant === 'ghost' && css`
    background-color: transparent;
    border: none;
    color: ${theme.colors.primary};
    box-shadow: none;
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
  `}

  ${({ size, theme }) => size === 'small' && css`
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: ${theme.typography.fontSizes.xs};
  `}

  ${({ size, theme }) => size === 'large' && css`
    padding: ${theme.spacing.md} ${theme.spacing.xl};
    font-size: ${theme.typography.fontSizes.lg};
  `}

  ${media.md`
    padding: ${props => props.size === 'small' ? `${props.theme.spacing.xs} ${props.theme.spacing.sm}` : 
               props.size === 'large' ? `${props.theme.spacing.lg} ${props.theme.spacing.xxl}` :
               `${props.theme.spacing.sm} ${props.theme.spacing.lg}`};
    font-size: ${props => props.size === 'small' ? props.theme.typography.fontSizes.sm :
                         props.size === 'large' ? props.theme.typography.fontSizes.xl :
                         props.theme.typography.fontSizes.md};
  `}

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.medium};

    ${({ variant, theme }) => variant === 'primary' && css`
      background-color: ${theme.colors.secondary};
    `}

    ${({ variant, theme }) => variant === 'secondary' && css`
      background-color: ${theme.colors.accent || theme.colors.primary};
    `}

    ${({ variant, theme }) => variant === 'success' && css`
      background-color: ${theme.colors.successDark || theme.colors.success};
      opacity: 0.9;
    `}

    ${({ variant, theme }) => variant === 'warning' && css`
      background-color: ${theme.colors.warningDark || theme.colors.warning};
      opacity: 0.9;
    `}

    ${({ variant, theme }) => variant === 'error' && css`
      background-color: ${theme.colors.errorDark || theme.colors.error};
      opacity: 0.9;
    `}

    ${({ variant, theme }) => variant === 'outline' && css`
      background-color: ${theme.colors.primary};
      color: ${theme.colors.text.inverse};
      border-color: ${theme.colors.primary};
      box-shadow: ${theme.shadows.small};
    `}

    ${({ variant, theme }) => variant === 'ghost' && css`
      background-color: ${theme.colors.primaryLight || `${theme.colors.primary}20`};
      box-shadow: none;
    `}
  }

  &:active:not(:disabled) {
    transform: translateY(0px);
    box-shadow: ${props => props.variant === 'outline' || props.variant === 'ghost' ? 'none' : props.theme.shadows.small};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
    
    ${({ variant, theme }) => variant === 'outline' && css`
       background-color: transparent;
       color: ${theme.colors.text.disabled};
       border-color: ${theme.colors.border};
    `}

    ${({ variant, theme }) => variant === 'ghost' && css`
       background-color: transparent;
       color: ${theme.colors.text.disabled};
    `}
  }
`;

// Icon Button component
export const IconButton = styled.button`
  position: ${props => props.position || 'static'};
  ${props => props.position === 'absolute' && css`
    right: ${props.theme.spacing.sm};
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
  `}
  
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSizes.lg};
  color: ${props => props.color || props.theme.colors.text.secondary};
  padding: ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.borderRadius.small};
  transition: color 0.2s ease, background-color 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${props => props.size === 'large' ? '48px' : props.size === 'small' ? '32px' : '40px'};
  min-height: ${props => props.size === 'large' ? '48px' : props.size === 'small' ? '32px' : '40px'};

  &:hover:not(:disabled) {
    color: ${props => props.hoverColor || props.theme.colors.text.primary};
    background-color: ${props => props.theme.colors.hoverBackground || `${props.theme.colors.primary}10`};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

// Floating Action Button
export const FloatingActionButton = styled(Button)`
  position: fixed;
  bottom: ${props => props.theme.spacing.xl};
  right: ${props => props.theme.spacing.xl};
  width: 56px;
  height: 56px;
  border-radius: 50%;
  padding: 0;
  box-shadow: ${props => props.theme.shadows.large};
  z-index: 1000;

  &:hover:not(:disabled) {
    box-shadow: ${props => props.theme.shadows.xlarge || props.theme.shadows.large};
    transform: scale(1.1);
  }

  ${media.md`
    width: 64px;
    height: 64px;
    font-size: ${props => props.theme.typography.fontSizes.xl};
  `}
`;

// Button Group container
export const ButtonGroup = styled.div`
  display: inline-flex;
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};

  ${Button} {
    border-radius: 0;
    border-right: 1px solid ${props => props.theme.colors.border};
    box-shadow: none;

    &:first-child {
      border-radius: ${props => props.theme.borderRadius.medium} 0 0 ${props => props.theme.borderRadius.medium};
    }

    &:last-child {
      border-radius: 0 ${props => props.theme.borderRadius.medium} ${props => props.theme.borderRadius.medium} 0;
      border-right: none;
    }

    &:only-child {
      border-radius: ${props => props.theme.borderRadius.medium};
      border-right: none;
    }

    &:hover:not(:disabled) {
      transform: none;
      z-index: 1;
      position: relative;
    }
  }
`;

// Toggle Button
export const ToggleButton = styled(Button)`
  ${({ active, theme }) => active && css`
    background-color: ${theme.colors.primary};
    color: ${theme.colors.text.inverse};
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover:not(:disabled) {
      background-color: ${theme.colors.secondary};
    }
  `}

  ${({ active, variant, theme }) => !active && variant === 'outline' && css`
    background-color: transparent;
    color: ${theme.colors.text.secondary};
    border-color: ${theme.colors.border};
  `}
`;

// Link Button (styled like a button but behaves like a link)
export const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.text.inverse};
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.typography.fontSizes.sm};
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  font-family: ${props => props.theme.typography.fontFamily};
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
  text-decoration: none;
  box-shadow: ${props => props.theme.shadows.small};

  ${({ variant, theme }) => variant === 'outline' && css`
    background-color: transparent;
    border: 1px solid ${theme.colors.primary};
    color: ${theme.colors.primary};
    box-shadow: none;
  `}

  &:hover {
    background-color: ${props => props.theme.colors.secondary};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.medium};
    text-decoration: none;

    ${({ variant, theme }) => variant === 'outline' && css`
      background-color: ${theme.colors.primary};
      color: ${theme.colors.text.inverse};
      border-color: ${theme.colors.primary};
      box-shadow: ${theme.shadows.small};
    `}
  }

  &:active {
    transform: translateY(0px);
    box-shadow: ${props => props.theme.shadows.small};
  }

  ${media.md`
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
    font-size: ${props => props.theme.typography.fontSizes.md};
  `}
`;

// Submit Button (specialized for forms)
export const SubmitButton = styled(Button)`
  width: 100%;
  margin-top: ${props => props.theme.spacing.md};

  ${media.sm`
    width: auto;
    min-width: 120px;
  `}
`;

// Close Button (specialized for modals/dialogs)
export const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${props => props.theme.spacing.sm};
  right: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSizes.xl};
  z-index: 10;

  &:hover:not(:disabled) {
    color: ${props => props.theme.colors.text.primary};
    background-color: ${props => props.theme.colors.errorLight || `${props.theme.colors.error}20`};
  }
`;

// Loading Button (shows loading state)
export const LoadingButton = styled(Button)`
  ${({ loading }) => loading && css`
    pointer-events: none;
    opacity: 0.7;
    cursor: not-allowed;
  `}
`;

// Button with badge/notification
export const BadgeButton = styled(Button)`
  position: relative;

  &::after {
    content: '${props => props.badge}';
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: ${props => props.badgeColor || props.theme.colors.error};
    color: ${props => props.theme.colors.text.inverse};
    border-radius: 50%;
    width: 20px;
    height: 20px;
    font-size: ${props => props.theme.typography.fontSizes.xs};
    display: ${props => props.badge ? 'flex' : 'none'};
    align-items: center;
    justify-content: center;
    font-weight: ${props => props.theme.typography.fontWeights.bold};
  }
`;

// Dropdown Button
export const DropdownButton = styled(Button)`
  position: relative;

  &::after {
    content: '▼';
    margin-left: ${props => props.theme.spacing.xs};
    font-size: ${props => props.theme.typography.fontSizes.xs};
    transition: transform 0.2s ease;
  }

  ${({ open }) => open && css`
    &::after {
      transform: rotate(180deg);
    }
  `}
`;